import axios from 'axios';
import moment from 'moment';
import isMobile from 'mobile-device-detect';

// eslint-disable-next-line import/no-cycle
import { store } from '@/store/store';

// eslint-disable-next-line no-unused-vars
// import { FEATURE_NAMES } from '@/utils/constants';

export class Timer {
    constructor(fn, t) {
        let timerObj = setInterval(fn, t);
        this.stop = () => {
            if (timerObj) {
                clearInterval(timerObj);
                timerObj = null;
            }
            return this;
        };
        this.start = () => {
            if (!timerObj) {
                this.stop();
                timerObj = setInterval(fn, t);
            }
            return this;
        };
    }
}

// https://stackoverflow.com/questions/2283566/how-can-i-round-a-number-in-javascript-tofixed-returns-a-string
export function toFixedNumber(num, digits, base = 10) {
    // eslint-disable-next-line no-restricted-properties
    const pow = Math.pow(base, digits);
    return Math.round(num * pow) / pow;
}

export function getSpeedMarkerColour(speed) {
    switch (true) {
        case speed <= 5:
            return { fillColor: '#DCE4EE ', strokeColor: '#303842' };
        case speed > 5 && speed <= 60:
            return { fillColor: '#0cdb8f', strokeColor: '#303842' };
        case speed > 60 && speed <= 90:
            return { fillColor: '#ff8629', strokeColor: '#303842' };
        case speed > 90 && speed <= 110:
            return { fillColor: '#fb1908', strokeColor: '#303842' };
        case speed > 110:
            return { fillColor: '#700069', strokeColor: '#303842' };
        default:
            return { fillColor: '#000', strokeColor: '#303842' };
    }
}

export function setColour(status) {
    let color;
    switch (status.toLowerCase()) {
        case 'pending':
            color = '#c2907f';
            break;
        case 'enroute':
            color = '#4ed2ff';
            break;
        case 'arrived':
            color = '#2b93ff';
            break;
        case 'departed':
            color = '#ffb42b';
            break;
        case 'cancelled':
            color = '#ff5245';
            break;
        case 'failed':
            color = '#f44336';
            break;
        case 'delayed':
        case 'on hold':
            color = '#384553';
            break;
        case 'complete':
            color = '#0bda8e';
            break;
        case 'started':
            color = '#4ED2FF';
            break;
        default:
        // do nothing
    }
    return color;
}

export function getStopStatusColour(status) {
    switch (status.toLowerCase()) {
        case 'pending':
            return {
                fillColor: '#c2907f',
                strokeColor: '#c2907f'
            };
        case 'enroute':
            return {
                fillColor: '#4ed2ff',
                strokeColor: '#4ed2ff'
            };
        case 'arrived':
            return {
                fillColor: '#2b93ff',
                strokeColor: '#2b93ff'
            };
        case 'departed':
            return {
                fillColor: '#ffb42b',
                strokeColor: '#ffb42b'
            };
        case 'complete':
            return {
                fillColor: '#0bda8e',
                strokeColor: '#0bda8e'
            };
        case 'delayed':
            return {
                fillColor: '#384553',
                strokeColor: '#384553'
            };
        case 'cancelled':
            return {
                fillColor: '#ff5245',
                strokeColor: '#ff5245'
            };
        default: {
            return {};
        }
    }
}

export function getShipmentStatusColour(status) {
    switch (status.toLowerCase()) {
        case 'pending':
            return {
                fillColor: '#c2907f',
                strokeColor: '#c2907f'
            };
        case 'enroute to pickup':
            return {
                fillColor: '#4ed2ff',
                strokeColor: '#4ed2ff'
            };
        case 'arrived at pickup':
            return {
                fillColor: '#2b93ff',
                strokeColor: '#2b93ff'
            };
        case 'enroute to drop':
            return {
                fillColor: '#4ed2ff',
                strokeColor: '#4ed2ff'
            };
        case 'arrived at drop':
            return {
                fillColor: '#2b93ff',
                strokeColor: '#2b93ff'
            };
        case 'departed':
            return {
                fillColor: '#ffb42b',
                strokeColor: '#ffb42b'
            };
        case 'picked up':
            return {
                fillColor: '#0bda8e',
                strokeColor: '#0bda8e'
            };
        case 'complete':
            return {
                fillColor: '#0bda8e',
                strokeColor: '#0bda8e'
            };
        case 'delayed':
            return {
                fillColor: '#384553',
                strokeColor: '#384553'
            };
        case 'cancelled':
            return {
                fillColor: '#ff5245',
                strokeColor: '#ff5245'
            };
        default: {
            return {};
        }
    }
}

const unlicensedTeamAlertMessage = {
    class: 'sm-modal-container',
    title: 'Manage Billing Online - Unavailable',
    body: `If you are paying by invoice or have an enterprise agreement, please contact our <a style="color:#2b93ff;" href="mailto:sales@locate2u.com">Sales Team</a> or your account manager.
    <br />
    <br />

    If you have a mobile app store subscription, you will need to manage your billing there.
    <br />
    <br />

    Contact our <a style = "color:#2b93ff;" href = "mailto:support@locate2u.com">Support Team</a> if neither of the above apply to you.
    `,
    buttons: ['OK']
};

export function createCancellationTokenSource() {
    return axios.CancelToken.source();
}

/**
 *
 * @param endpoint
 * @param payload
 * @param isLoggedIn
 * @param {import("axios").CancelToken} cancellationToken
 * @returns {Promise<unknown>}
 */
export async function handleRequests(
    endpoint,
    payload = { method: 'get' },
    isLoggedIn = true,
    cancellationToken = null
) {
    // If entry for Backend server is found,
    // the dev server will connect to cloud server for api hits
    const url =
        process.env.VUE_APP_BACKEND_SERVER && endpoint.startsWith('/api/')
            ? process.env.VUE_APP_BACKEND_SERVER + endpoint
            : endpoint;

    const request = Object.assign({}, payload, {
        url
    });

    if ((payload.method !== 'put' && isLoggedIn) || (payload.method === 'put' && payload.headers === undefined)) {
        const user = await window.auth.getUser();
        const accessToken = user.access_token;
        request.headers = Object.assign({}, payload.headers || {}, {
            Authorization: `Bearer ${accessToken}`
        });
    }

    return new Promise((resolve, reject) => {
        axios({
            ...request,
            cancelToken: cancellationToken
        }).then(
            (r) => {
                const { data, status } = r;
                resolve({ data, status });
            },
            (e) => {
                if (!e.response) 
                    return;

                const { data, status } = e.response;

                if (data && Array.isArray(data) && data.length) {
                    const unlicensed = data.find((x) => x.errorCode === 'MUST_HAVE_LICENSE');
                    if (unlicensed) {
                        // hide the loader (just in case it's loaded)
                        const loader = {
                            isLoading: false,
                            loadingText: null
                        };
                        store.commit('CHANGE_LOADING_STATE', loader, { root: true });

                        if (window.location.href.includes('/billing/profile')) {
                            // eslint-disable-next-line prefer-promise-reject-errors
                            reject({ data, status });
                        } else {
                            window.location.href = '/billing/profile';
                        }
                    }
                }

                /* eslint-disable prefer-promise-reject-errors */
                reject({ data, status });
            }
        );
    });
}

export async function redirectToBillingPortal(redirectUrl, alertMessage) {
    const api = `/api/teams/billing-portal-url/?redirectUrl=${redirectUrl}`;
    const payload = {
        method: 'get'
    };

    try {
        const { data } = await handleRequests(api, payload);
        window.location.href = data;
    } catch (error) {
        const showAlertMessage = alertMessage ?? unlicensedTeamAlertMessage;

        window.messageBox
            .show(showAlertMessage)
            // eslint-disable-next-line consistent-return
            .then((response) => {
                if (response && response === 'OK') {
                    return true;
                }
            });
    }
}

export async function downloadFile(data, reportName) {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', reportName);
    document.body.appendChild(link);
    link.click();
}

export async function openFile(data, reportName, target = '_blank', type = 'application/pdf') {
    const url = window.URL.createObjectURL(new Blob([data], { type }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('target', target);
    document.body.appendChild(link);
    link.click();
}

export function getRegionCoordinates(regionCode) {
    // these are currently the only regions based from the regions code. will add more as we expand our coverage.
    const regions = {
        AU: {
            latitude: -25.2743988,
            longitude: 133.7751312
        },
        TR: {
            latitude: -25.2743988,
            longitude: 133.7751312
        },

        OCA: {
            latitude: -6.3149929,
            longitude: 143.9555511
        },
        IN: {
            latitude: 20.5936832,
            longitude: 78.962883
        },
        PH: {
            latitude: 12.8797207,
            longitude: 121.7740173
        },
        ZA: {
            latitude: -30.559482,
            longitude: 22.937506
        }
    };

    return Object.prototype.hasOwnProperty.call(regions, regionCode) ? regions[regionCode] : regions[TR];
}

export function imageCacheBuster(image) {
    let x = image;
    if (x != null) {
        x = `${x}?${new Date().getTime()}`;
    }
    return x;
}

export function isPhoneValid(phone) {
    if (!phone) {
        return true;
    }

    return /^[0-9*#+() -]+$/.test(phone);
}

/**
 * Determines whether the given email is valid.
 *
 * This function first checks if the value exists and is a string. If not, it immediately
 * marks the email as invalid.
 *
 * It then checks if the email follows standard email formatting rules:
 *  - A valid local part (before the '@'): allows alphanumeric characters and common
 *      symbols, with optional dot-separated segments.
 *  - A valid domain part (after the '@'): one or more domain labels (e.g., example.com), where each label may contain
 *        letters, numbers, or hyphens, and the final segment must be a valid TLD or Top-Level Domain (e.g., .com, .au, .io).
 *
 * If the email matches this pattern, the function returns true (valid); otherwise false (invalid).
 */
export function isEmailValid(email) {
    if (!email || typeof email !== 'string') {
        return false;
    }

    const validEmailPattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    return validEmailPattern.test(email);
}

export function isValidImageFileType(ext) {
    if (
        ext === 'image/jpeg' ||
        ext === 'image/jpg' ||
        ext === 'image/png' ||
        ext === 'image/gif' ||
        ext === 'image/bmp' ||
        ext === 'image/webp'
    ) {
        return true;
    }

    return false;
}

export function isValidFileName(fileName) {
    // Define the regex pattern to disallow \ / : * ? " < > |
    const invalidChars = /[\\/:*?"<>|]/;

    // Check if the file name contains any invalid characters
    if (invalidChars.test(fileName)) {
        return false;
    }

    // Check if the file name is empty or too long
    if (fileName.length === 0 || fileName.length > 100) {
        return false;
    }

    // If all checks pass, the file name is valid
    return true;
}

export function minStopDuration(duration) {
    if (!duration) 
        return true;

    if (duration < 0) 
        return false;

    return true;
}

export function maxStopDuration(duration) {
    if (!duration) 
        return true;

    if (duration > 1440) 
        return false;

    return true;
}

export function isStopDurationNull(duration) {
    if (!duration) 
        return true;

    const regExp = /[a-zA-Z]/g;

    return !regExp.test(duration);
}

export function isValidEmail(email) {
    return String(email)
        .toLowerCase()
        .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        );
}

export function showErrorMessage(el, message, error) {
    if (error && error.data) {
        if (Array.isArray(error.data) && error.data.length > 0) {
            error.data.forEach((e) => {
                el.$notify({
                    message: e.message,
                    type: 'danger'
                });
            });
        } else {
            el.$notify({
                message: error.data,
                type: 'danger'
            });
        }
    } else {
        el.$notify({
            message,
            type: 'danger'
        });
    }
}

/**
 * Displays clear API response error messages returned from activities that use ActivityRequest<Trequest, TResponse>
 */
export function showActivityErrorMessage(el, message, error) {
    if (error && error.data && error.status === 400) {
        if (Array.isArray(error.data) && error.data.length > 0) {
            error.data.forEach((e) => {
                el.$notify({
                    message: e,
                    type: 'danger'
                });
            });
        } else if (typeof error.data === 'string') {
            el.$notify({
                message: error.data,
                type: 'danger'
            });
        } else {
            el.$notify({
                message,
                type: 'danger'
            });
        }
    } else {
        el.$notify({
            message,
            type: 'danger'
        });
    }
}

export function isEmptyOrSpaces(str) {
    return str === null || str.match(/^ *$/) !== null;
}

export function isAValidCoordinate(coordinateString) {
    if (!coordinateString) {
        return false;
    }

    const expression = /^([-+]?)([\d]{1,2})(((\.)(\d+)(,)))(\s*)(([-+]?)([\d]{1,3})((\.)(\d+))?)$/;
    return expression.test(coordinateString);
}

export function getShortLocaleStandardDate() {
    // get user locale based on browser's userLanguage
    // usetLanguage is for IE browsers, languages is for all other modern browsers
    const userLocale = window.navigator.userLanguage || window.navigator.language;

    // get long date without time format (moment.js has no local implementation)
    const localeData = moment.localeData(userLocale);
    const llll = localeData.longDateFormat('llll');
    const lll = localeData.longDateFormat('lll');
    const ll = localeData.longDateFormat('ll');

    return llll.replace(lll.replace(ll, ''), '');
}

export function convertUtcTimeStringToLocalDate(timeStamp, utcOffset) {
    const timeStampWithUtcRemoved = timeStamp.replace(/Z|z/, '');
    const convertedDate = new Date(timeStampWithUtcRemoved);

    // getTime in milliseconds so we have to convert utcOffset to milliseconds
    return new Date(convertedDate.getTime() + utcOffset * 60000);
}

export function camelCaseToSentenceCase(text) {
    const result = text.replace(/([A-Z])/g, ' $1');
    return result.charAt(0).toUpperCase() + result.slice(1);
}

/* from harvestine formula
 * https://cloud.google.com/blog/products/maps-platform/how-calculate-distances-map-maps-javascript-api
 */
export function getDistanceBetweenMarkers(mk1, mk2) {
    const R = 6371.071; // Radius of the Earth in km
    const rlat1 = mk1.latitude * (Math.PI / 180); // Convert degrees to radians
    const rlat2 = mk2.latitude * (Math.PI / 180); // Convert degrees to radians
    const difflat = rlat2 - rlat1; // Radian difference (latitudes)
    const difflon = (mk2.longitude - mk1.longitude) * (Math.PI / 180); // Radian difference (longitudes)

    const d =
        2 *
        R *
        Math.asin(
            Math.sqrt(
                Math.sin(difflat / 2) * Math.sin(difflat / 2) +
                    Math.cos(rlat1) * Math.cos(rlat2) * Math.sin(difflon / 2) * Math.sin(difflon / 2)
            )
        );
    return d; // in km;
}
export function getTimeInMinutes(currentTime, offset) {
    // Note that in daylight saving switchover scenarios, this won't be the number of minutes since midnight
    const momentTime = moment(currentTime).utcOffset(offset);
    return momentTime.hour() * 60 + momentTime.minute();
}
export function getTimeInMs(timestamp) {
    const time = moment
        .utc(timestamp)
        .local()
        .format('HH:mm:ss')
        .split(':');
    // hours minutes seconds
    return (Number(time[0]) * 3600 + Number(time[1]) * 60 + Number(time[2])) * 1000;
}

export function showTeamRegionControl(currentStore, moduleTeamRegionId) {
    return currentStore.hasAssetAccess && currentStore.teamRegions && currentStore.teamRegions.length > 0;
    // return (
    //     !(currentStore.teamRegions && currentStore.teamRegions.length > 0) ||
    //     !!currentStore.user.teamRegionId ||
    //     !!moduleTeamRegionId
    // );
}

export function filterMembersByTeamRegionId(
    allTeamRegions,
    allRawMembers,
    defaults,
    teamRegionId,
    strictTeamRegionFilter,
    includeChildTeamRegion = false
) {
    // no team members
    if (!Array.isArray(allRawMembers) || allRawMembers.length === 0) 
        return [];

    // update empty fullname to
    const allMembers = allRawMembers.map((x) => {
        if (!x.fullName || x.fullName === ' ') 
            return { ...x, fullName: x.email };
        return { ...x, memberCode: x.memberCode || null };
    });

    // no team region setup
    if (!Array.isArray(allTeamRegions) || allTeamRegions.length === 0) {
        if (defaults) 
            return [defaults, ...allMembers];
        return [...allMembers];
    }

    // all members
    if (!teamRegionId || teamRegionId === 0) {
        if (defaults) 
            return [defaults, ...allMembers];
        return [...allMembers];
    }

    let filtered = [];
    if (teamRegionId === -1) {
        // only members without team region
        filtered = allMembers.filter((x) => {
            // null SHOULD suffice
            return x.teamRegionId === undefined || x.teamRegionId === null || x.teamRegionId === -1;
        });
    } else if (strictTeamRegionFilter) {
        // specific
        filtered = allMembers.filter((x) => {
            return x.teamRegionId === teamRegionId;
        });
    } else {
        // include members without TR set
        filtered = allMembers.filter((x) => {
            const matchesTeamRegion = !x.teamRegionId || x.teamRegionId === teamRegionId;
            const matchesChildRegion = includeChildTeamRegion && x.parentTeamRegionId === teamRegionId;
            return matchesTeamRegion || matchesChildRegion;
        });
    }

    // return at least the default
    if (defaults) 
        return [defaults, ...filtered];
    return [...filtered];
}

export function filterMemberOrCarrierssByTeamRegionId({
    allTeamRegions,
    allRawMembers,
    defaults,
    teamRegionId,
    strictTeamRegionFilter,
    allRawTeamCarriers,
    showChildTeamRegion,
    listOfTeamRegion
}) {
    let allMembers = [];
    let allTeamCarriers = [];

    // team members
    if (Array.isArray(allRawMembers) && allRawMembers.length > 0) {
        // update empty fullname to
        allMembers = allRawMembers.map((x) => {
            const fullName = !x.fullName || x.fullName === ' ' ? x.email : x.fullName;
            return {
                publicUserId: x.publicUserId,
                fullName,
                email: x.email,
                teamRegionId: x.teamRegionId,
                memberCode: x.memberCode || null,
                type: 'member',
                selectable: true,
                memberOrCarrierId: x.publicUserId,
                isAvailableBasedOnRosterConfiguration: x.isAvailableBasedOnRosterConfiguration
            };
        });
    }

    // team carriers
    if (Array.isArray(allRawTeamCarriers) && allRawTeamCarriers.length > 0) {
        allTeamCarriers = allRawTeamCarriers.map((x) => {
            return {
                fullName: x.carrierTeam.company,
                teamRegionId: x.teamRegionId,
                carrierTeamId: x.carrierTeamId,
                type: 'carrier',
                selectable: true,
                memberOrCarrierId: x.carrierTeamId
            };
        });
    }

    let allList = [];
    if (allMembers.length && allTeamCarriers.length) {
        allList = [...allMembers, { selectable: false }, ...allTeamCarriers];
    } else {
        allList = [...allMembers, ...allTeamCarriers];
    }

    // no team region setup
    if (!Array.isArray(allTeamRegions) || allTeamRegions.length === 0) {
        if (defaults) 
            return [defaults, ...allList];
        return [...allList];
    }

    // all members
    if (!teamRegionId || teamRegionId === 0) {
        if (defaults) 
            return [defaults, ...allList];
        return [...allList];
    }

    let filtered = [];
    if (teamRegionId === -1) {
        // only those without team region
        filtered = allList.filter((x) => {
            // null SHOULD suffice
            return !x.selectable || x.teamRegionId === undefined || x.teamRegionId === null || x.teamRegionId === -1;
        });
    } else if (strictTeamRegionFilter) {
        // specific
        filtered = allList.filter((x) => {
            return (
                !x.selectable ||
                x.teamRegionId === teamRegionId ||
                (showChildTeamRegion &&
                    allTeamRegions.some((y) => {
                        return y.teamRegionId === x.teamRegionId && y.parentTeamRegionId === teamRegionId;
                    }))
            );
        });
    } else {
        // include members and carriers without TR set
        filtered = allList.filter((x) => {
            return (
                !x.selectable ||
                !x.teamRegionId ||
                x.teamRegionId === teamRegionId ||
                listOfTeamRegion.includes(x.teamRegionId)
            );
        });
    }

    // return at least the default
    if (defaults) 
        return [defaults, ...filtered];
    return [...filtered];
}

export function getTeamRegionName(teamRegionId, teamRegions) {
    if (teamRegionId && teamRegions) {
        const tr = teamRegions.find((x) => x.teamRegionId === teamRegionId);
        return tr?.name;
    }
    return null;
}

export class CarrierPreBookingQuestionsMapper {
    mapAsync = async (carrierTeamId) => {
        try {
            const api = `/api/teams/carriers/?carrierTeamId=${carrierTeamId}`;
            const { data } = await handleRequests(api);

            if (
                data.carrierTeam &&
                data.carrierTeam.questions &&
                data.carrierTeam.questions.booking &&
                data.carrierTeam.questions.booking.length
            ) {
                const result = this.map({
                    carrierBookingQuestions: data.carrierTeam.questions.booking,
                    customerMappings: data.questionCustomFieldsMapping
                });

                return {
                    ...result,
                    success: true
                };
            }

            return {
                fixedMappingList: [],
                newAnswersList: [],
                combinedList: [],
                success: true
            };
        } catch (error) {
            return {
                success: false,
                error
            };
        }
    };

    map = ({ carrierBookingQuestions, customerMappings }) => {
        const mappings = customerMappings || [];

        // list of questions that are mapped to custom fields
        // or has default and not overridable
        const fixedMappingList = mappings.filter(
            (x) => x.mapping === 'CustomField' || (x.mapping === 'SetDefault' && !x.overridable)
        );

        // find carrier questions that can be overridden or no mapping at all
        const overridableQuestions = carrierBookingQuestions.filter((q) => {
            return fixedMappingList.findIndex((m) => m.name === q.name) === -1;
        });

        const newAnswersList = [];
        for (let ndx = 0; ndx < overridableQuestions.length; ndx++) {
            const defaultAnswer = mappings.find((q) => q.name === overridableQuestions[ndx].name);
            if (defaultAnswer) {
                if (defaultAnswer.mapping && defaultAnswer.mapping !== 'Ignore') {
                    // populate with default answer
                    newAnswersList.push({
                        ...defaultAnswer,
                        defaultValue: this.parseValue(overridableQuestions[ndx].type, defaultAnswer.defaultValue),
                        ...overridableQuestions[ndx]
                    });
                }
            } else {
                // no mapping but we'll show if it's required
                // eslint-disable-next-line no-lonely-if
                if (overridableQuestions[ndx].isRequired) {
                    newAnswersList.push({
                        ...overridableQuestions[ndx],
                        defaultValue: null,
                        mapping: 'SetDefault' // there's no mapping; we'll set it 'SetDefault' in order to assign answer
                    });
                }
            }
        }

        // include questions that are mapped to custom fields
        const combinedList = this.combine(fixedMappingList, newAnswersList);
        return {
            fixedMappingList,
            newAnswersList,
            combinedList
        };
    };

    combine = (fixedMappingList, newAnswersList) => {
        const combined = [...fixedMappingList, ...newAnswersList];
        return combined.map((x) => {
            return {
                name: x.name,
                type: x.type,
                mapping: x.mapping,
                defaultValue: this.setValue(x),
                customFieldName: x.customFieldName
            };
        });
    };

    parseValue = (type, value) => {
        switch (type) {
            case 'CheckBox':
                return value === 'true';
            default:
                return value;
        }
    };

    setValue = (answer) => {
        if (answer.mapping === 'SetDefault') {
            if (answer.type === 'CheckBox') {
                return (answer.defaultValue || false).toString();
            }
            return answer.defaultValue ? answer.defaultValue.toString() : null;
        }

        if (answer.mapping === 'AskWhenBooking') {
            return answer.defaultValue ? answer.defaultValue.toString() : null;
        }

        return null;
    };
}

export class SearchGuard {
    constructor(filters = {}) {
        this.filters = filters;
    }

    // Aims to avoid calling the Api for the same parameters by
    // comparing the previous filters and new filters.
    // returns true if different, and false if the same
    shouldSearch = (newFilters) => {
        let shouldSearch = false;
        Object.keys(newFilters).forEach((q) => {
            shouldSearch = shouldSearch || (newFilters[q] || null) !== (this.filters[q] || null);
        });

        if (shouldSearch) {
            this.filters = Object.assign(this.filters, newFilters);
        }
        return shouldSearch;
    };
}

// sum of digits of a number
function sumDigit(value) {
    let number = value;
    let result = 0;
    while (number > 0) {
        result += number % 10;
        number = parseInt(number / 10, 10);
    }
    return result;
}
export function isValidIMEI(value) {
    let number = value;
    if (!number) {
        return false;
    }
    // Converting the number into
    // String for finding length
    const imeiString = number.toString();
    const len = imeiString.length;

    if (len !== 15) {
        return false;
    }

    let sum = 0;
    for (let i = len; i >= 1; i--) {
        let digit = number % 10;

        // Doubling every alternate digit
        if (i % 2 === 0) {
            digit *= 2;
        }

        // Finding sum of the digits
        sum += sumDigit(digit);
        number = parseInt(number / 10, 10);
    }

    const result = sum % 10 === 0;
    return result;
}

export function formatCondition(conditionRow, rateZones, teamTags) {
    let tempConditionRow = conditionRow;
    const numberPattern = /\d+/g;

    if (tempConditionRow.includes('In') || tempConditionRow.includes('NotIn')) {
        const match = tempConditionRow.match(numberPattern);

        match.forEach((zoneId, index) => {
            const zone = rateZones.find((x) => x.rateZoneId === Number(zoneId));
            tempConditionRow = tempConditionRow.replaceAll(zoneId, zone.name);
        });
    }

    if (tempConditionRow.includes('TagIds.Contains(')) {
        const tagId = tempConditionRow.match(/\d+/g)[0];
        const tag = teamTags.find((x) => x.tagId === Number(tagId));

        tempConditionRow = tempConditionRow.replaceAll(tagId, `'${tag.name}''`);
        tempConditionRow = tempConditionRow.replace('TagIds.Contains', 'Tag is ');
        tempConditionRow = tempConditionRow.replaceAll('(', '');
        tempConditionRow = tempConditionRow.replaceAll(')', '');
    }

    tempConditionRow = tempConditionRow.replaceAll('r.', '');
    tempConditionRow = tempConditionRow.replace('&&', '');
    tempConditionRow = tempConditionRow.replace('||', '');
    tempConditionRow = tempConditionRow.replaceAll('.', ' ');
    tempConditionRow = tempConditionRow.replace('==', '=');
    return tempConditionRow;
}

export function formatOperator(data) {
    if (data.includes('&&')) {
        return 'And';
    }

    if (data.includes('||')) {
        return 'Or';
    }

    return null;
}

export function checkForOperator(data) {
    if (data.includes('&&') || data.includes('||')) {
        return true;
    }

    return false;
}

export function isValidInvoiceNumberAffix(value) {
    if (value === null || value === '') {
        return true;
    }

    const regExp = /^[A-Za-z0-9-]+$/;

    return regExp.test(value);
}

export function titleCase(str) {
    return str
        .toLowerCase()
        .split(' ')
        .map((x) => x.charAt(0).toUpperCase() + x.slice(1))
        .join(' ');
}

export function canRouteAndShow(route, user) {
    if (route.meta?.security?.isSupportOnly) {
        if (!user.isSupportUser || !user.isSupportAdminUser) 
            return false;

        if (route.meta?.security?.isSupportOnlyWhileJoinedToTeam) {
            // only show if user is joined to a team
            return user.supportingTeam;
        }
        // show if Support and currently not joined to a team
        return !user.supportingTeam;
    }

    if (route.meta?.security?.roles && user.role) {
        if (!route.meta.security?.roles.includes(user.role)) 
            return false;
    }

    if (route.meta?.security?.featureRestrictions && !user.features) 
        return false;

    if (route.meta?.security?.featureRestrictions && user.features) {
        if (!user.features.some((feature) => route.meta.security.featureRestrictions.includes(feature))) {
            return false;
        }
    }

    if (route.meta?.security?.isVisible) {
        if (
            route.meta.security.isVisible.includes('hasTeam') &&
            !user.hasTeam &&
            !route.meta.security.isVisible.includes('standalone')
        )
            return false;

        if (
            route.meta.security.isVisible.includes('standalone') &&
            user.hasTeam &&
            !route.meta.security.isVisible.includes('hasTeam')
        )
            return false;

        const isDesktop = !isMobile.isMobile;

        if (route.meta.security.isVisible.includes('desktopOnly') && !isDesktop) {
            return false;
        }
    }

    return true;
}

export function sideBarMenuBuilder(menus, useNewOptimiseStops, useNewMap, menuBadges) {
    const menu = [
        {
            id: 'dashboard',
            name: menus.dashboard,
            icon: 'dashboard',
            path: '/dashboard'
        },
        {
            id: 'map',
            name: menus.map,
            icon: 'map',
            path: useNewMap ? '/mapview2' : '/mapview'
        },
        {
            id: 'customers',
            name: menus.customers,
            icon: 'contact_page',
            path: '/customers/list'
        },
        {
            id: 'trips',
            name: menus.trips,
            icon: 'commute',
            path: '/trips/list'
        },
        // Will be enabled very soon. Deliberately commented for this release. Icon and text already approved by Mark.
        // Just delaying showing the menu to all customers.
        {
            id: 'jobs',
            name: menus.jobs,
            icon: 'emoji_transportation',
            path: '/jobs/filter'
        },
        {
            id: 'stops',
            name: menus.stops,
            icon: 'place',
            path: '/stops/list'
        },
        {
            id: 'shipments',
            name: menus.shipments,
            icon: 'directions_car',
            path: '/shipments/list'
        },
        {
            id: 'orders',
            name: menus.orders,
            icon: 'receipt_long',
            path: '/orders'
        },
        {
            id: 'warehouse-web-app',
            name: menus.warehouse,
            icon: 'store',
            path: `${process.env.VUE_APP_TEAM_PORTAL_URL}/warehouse-inventory`,
            isRoute: false,
            disableRoute: true,
            target: '_blank'
        },
        {
            id: 'issues',
            name: menus.issues,
            icon: 'report_problem',
            path: '/issues',
            notification: String(menuBadges.find((x) => x.id === 'issues').value)
        },
        {
            id: 'frequent-addresses',
            name: menus.frequentAddresses,
            icon: 'star',
            path: '/frequent-addresses'
        },
        {
            id: 'optimise-stops-mapbox',
            name: menus.optimizeStops,
            icon: 'local_shipping',
            path: '/optimise-stops'
        },
        {
            id: 'inventory',
            name: menus.item,
            icon: 'inventory',
            path: '/inventory/list'
        },
        {
            id: 'calendar',
            name: menus.calendarTimeline,
            icon: 'calendar_today',
            path: '/stops/calendar'
        },
        {
            name: menus.assetManagement.assetManagement,
            icon: 'work',
            children: [
                {
                    id: 'assets',
                    name: menus.assetManagement.assets,
                    path: '/assets/overview'
                },
                {
                    id: 'maintenance',
                    name: menus.assetManagement.schedules,
                    path: '/maintenance/schedules'
                },
                {
                    id: 'maintenance',
                    name: menus.assetManagement.upcoming,
                    path: '/maintenance/upcoming'
                }
            ]
        },
        {
            name: 'Reports',
            icon: 'assignment',
            children: [
                {
                    id: 'ignition-report',
                    name: 'Ignition On/Off',
                    path: '/reports/ignition-report'
                },
                {
                    id: 'location-history-report',
                    name: 'Location History',
                    path: '/reports/location-history-report'
                },
                {
                    id: 'safety-report',
                    name: 'Safety',
                    path: '/reports/safety-report'
                },
                {
                    id: 'safety-summary-report',
                    name: 'Safety Summary',
                    path: '/reports/safety-summary-report'
                },
                {
                    id: 'speeding-report',
                    name: 'Speeding',
                    path: '/reports/speeding-report'
                },
                {
                    id: 'stop-report',
                    name: 'Stops',
                    path: '/reports/stop-report'
                },
                {
                    id: 'stop-rating-report',
                    name: 'Stop Rating Report',
                    path: '/reports/stop-rating-report'
                },
                {
                    id: 'distance-report',
                    name: 'Travel Statistics',
                    path: '/reports/distance-report'
                },
                {
                    id: 'geofence-timesheet',
                    name: 'Geofence Report',
                    path: '/reports/geofence-report'
                },
                {
                    id: 'geofence-report',
                    name: 'Geofence Timesheet',
                    path: '/reports/geofence-timesheet'
                },
                {
                    id: 'driver-payment-summary',
                    name: 'Driver Payment Summary',
                    path: '/reports/driver-payment-summary'
                },
                {
                    id: 'sms-history',
                    name: 'Sms History',
                    path: '/reports/sms-history'
                },
                {
                    id: 'team-member-documents',
                    name: 'Team Member Documents',
                    path: '/reports/team-member-documents'
                }
            ]
        },
        {
            name: menus.gpsReports.gpsReports,
            icon: 'assignment',
            children: [
                {
                    id: 'gps-locations',
                    name: menus.gpsReports.locations,
                    path: '/gps-reports/locations'
                },
                {
                    id: 'gps-stops',
                    name: menus.gpsReports.stops,
                    path: '/gps-reports/stops'
                }
            ]
        },
        {
            name: 'Process Charges',
            icon: 'price_check',
            children: [
                {
                    id: 'process-charges-grid',
                    name: 'Process Charges Trips',
                    path: '/processcharges/trips'
                },
                {
                    id: 'process-charges-grid',
                    name: 'Process Charges Stops',
                    path: '/processcharges/stops'
                },
                {
                    id: 'process-charges-grid',
                    name: 'Process Charges Shipments',
                    path: '/processcharges/shipments'
                }
            ]
        },
        {
            name: 'Invoices',
            icon: 'receipt',
            children: [
                {
                    id: 'invoices-grid',
                    name: 'Team Member Invoice',
                    path: '/invoices/team-member-invoice'
                }
            ]
        },
        {
            name: 'Accounting',
            icon: 'paid',
            children: [
                {
                    id: 'team-member-invoices',
                    name: 'Team Member Invoices',
                    path: '/team-member-invoices'
                },
                {
                    id: 'customer-invoices',
                    name: 'Customer Invoices',
                    path: '/customer-invoices'
                },
                {
                    id: 'rates-engine',
                    name: menus.rates,
                    icon: 'price_change',
                    path: '/rates/list'
                },
                {
                    id: 'process-charges',
                    name: menus.processCharges,
                    icon: 'price_check',
                    path: '/processcharges/list'
                }
            ]
        },
        {
            id: 'team-standalone-settings-menu',
            name: menus.team.team,
            icon: 'group',
            path: '/team/standalone-member'
        },
        {
            id: 'billing-accounts-menu',
            name: menus.billing.menu,
            icon: 'payment',
            children: [
                {
                    id: 'billing-accounts',
                    name: menus.billing.accounts,
                    path: '/billing/accounts'
                },
                {
                    id: 'ea-list',
                    name: menus.billing.ealist,
                    path: '/billing/ea-list'
                }
            ]
        },
        {
            id: 'team-settings-menu',
            name: menus.team.settings,
            icon: 'settings',
            children: [
                {
                    id: 'team-profile',
                    name: menus.team.profile,
                    path: '/team/profile'
                },
                {
                    id: 'team-members',
                    name: menus.team.members,
                    path: '/team/team-members'
                },
                {
                    id: 'team-settings',
                    name: menus.team.configuration,
                    path: '/team/settings'
                },
                {
                    id: 'carriers',
                    name: 'Carriers',
                    path: '/team/carriers'
                },
                {
                    id: 'run-schedules',
                    name: menus.team.runSchedules,
                    path: '/team/run-schedules'
                },
                {
                    id: 'marketplace',
                    name: 'Carrier Marketplace',
                    path: '/marketplace'
                },
                {
                    id: 'warehouses',
                    name: 'Warehouses',
                    path: '/warehouses/list',
                    icon: 'store'
                },
                {
                    id: 'billing-profile',
                    name: 'Billing',
                    path: '/billing/profile'
                }
            ]
        }
    ];

    return menu;
}

export function timeSpanToTimeParts(timeSpan) {
    /**
     * timeSpan format:
     *    could be '1.02:03:04', including day
     *    or just '02:03:04'
     */

    let timeParts = {
        day: 0,
        hour: 0,
        minute: 0,
        second: 0,
        totalSeconds: 0
    };

    if (!timeSpan) {
        return timeParts;
    }

    let d = 0;
    let h = 0;
    let m = 0;
    let s = 0;

    const parts = timeSpan.split(':');
    if (parts.length === 3) {
        s = Number(parts[2]);
        m = Number(parts[1]);

        // try splitting days and hours
        const dayHourParts = parts[0].split('.');
        if (dayHourParts.length === 2) {
            h = Number(dayHourParts[1]);
            d = Number(dayHourParts[0]);
        } else if (dayHourParts.length === 1) {
            // only hour, no day
            h = Number(dayHourParts[0]);
        }
    }

    timeParts = {
        day: d,
        hour: h,
        minute: m,
        second: s,
        totalSeconds: d * 24 * 60 * 60 + h * 60 * 60 + m * 60 + s
    };

    return timeParts;
}

export const rowStatus = Object.freeze({
    ADD: 'add',
    EDIT: 'edit',
    DELETE: 'delete'
});

export function compareProperties(obj1, obj2, properties) {
    return properties.every((prop) => {
        if (typeof obj1[prop] === 'object' && typeof obj2[prop] === 'object') {
            return JSON.stringify(obj1[prop]) === JSON.stringify(obj2[prop]);
        }

        return obj1[prop] === obj2[prop];
    });
}
